import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Badge } from '@/components/ui/badge';
import { Plus, ShoppingCart, Gem, Coins, Sparkles, Circle, ChevronDown, ChevronUp } from 'lucide-react';
import { formatINR, formatCarats, nowISTDatetimeLocal, istDatetimeLocalToISO } from '@/lib/format';
import { ClassificationFormDialog } from './ClassificationFormDialog';
import { PurityFormDialog } from './PurityFormDialog';

// Tiny localStorage hook so the collapsed/expanded state persists per user.
function useCollapsiblePref(key, defaultValue = false) {
  const [open, setOpen] = useState(() => {
    try {
      const v = localStorage.getItem(key);
      if (v === '1') return true;
      if (v === '0') return false;
    } catch { /* noop */ }
    return defaultValue;
  });
  const setAndStore = (next) => {
    setOpen(next);
    try { localStorage.setItem(key, next ? '1' : '0'); } catch { /* noop */ }
  };
  return [open, setAndStore];
}

/**
 * MakePurchaseInline — Phase 10
 *
 * Collapsible card on the Vendor detail page so users can record a
 * purchase from this vendor without leaving the page.
 *
 * Closed by default (preference persisted per user) to keep the page clean.
 *
 * Supports:
 *  - Diamonds: pick existing packet OR create-new packet inline, then receipt.
 *  - Gold: weight + purity + optional rate + reference + note.
 *  - Color Stones / Pearls: disabled (Coming soon — module not built yet).
 */
export function MakePurchaseInline({ vendor, companyId, onPurchased }) {
  const [material, setMaterial] = useState('diamond');
  const [open, setOpen] = useCollapsiblePref(`vendor.${vendor?.id || 'unknown'}.make_purchase.open`, false);

  const handleSuccess = () => {
    // Auto-collapse after a successful purchase so the page returns to a clean state.
    try { setOpen(false); } catch { /* noop */ }
    onPurchased && onPurchased();
  };

  return (
    <Card className="p-5" data-testid="vendor-make-purchase-card">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-3 text-left"
        data-testid="vendor-make-purchase-toggle"
        aria-expanded={open}
      >
        <div>
          <div className="text-base font-medium text-[var(--ink-navy)] flex items-center gap-2">
            <ShoppingCart size={16} /> Make Purchase
          </div>
          <div className="text-xs text-slate-500">Record a new purchase from {vendor?.name}. Click to {open ? 'collapse' : 'expand'}.</div>
        </div>
        <div className="shrink-0 text-slate-500 mt-1">
          {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {open && (
        <div className="mt-4">
          <div className="flex flex-wrap gap-2 mb-3">
            <MatTab id="diamond" active={material} onClick={setMaterial} icon={Gem} label="Diamond" />
            <MatTab id="gold" active={material} onClick={setMaterial} icon={Coins} label="Gold" />
            <MatTab id="color_stone" active={material} onClick={setMaterial} icon={Sparkles} label="Color Stones" />
            <MatTab id="pearl" active={material} onClick={setMaterial} icon={Circle} label="Pearls" />
          </div>
          {material === 'diamond' && <CaratPurchaseForm material="diamond" vendor={vendor} companyId={companyId} onPurchased={handleSuccess} />}
          {material === 'gold' && <GoldPurchaseForm vendor={vendor} companyId={companyId} onPurchased={handleSuccess} />}
          {material === 'color_stone' && <CaratPurchaseForm material="color_stone" vendor={vendor} companyId={companyId} onPurchased={handleSuccess} />}
          {material === 'pearl' && <CaratPurchaseForm material="pearl" vendor={vendor} companyId={companyId} onPurchased={handleSuccess} />}
        </div>
      )}
    </Card>
  );
}

function MatTab({ id, active, onClick, icon: Icon, label, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onClick(id)}
      disabled={disabled}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs transition ${
        active === id
          ? 'border-[var(--ink-navy)] bg-[var(--ink-navy)] text-white'
          : disabled
          ? 'border-[var(--divider)] text-slate-400 cursor-not-allowed'
          : 'border-[var(--divider)] hover:bg-slate-50'
      }`}
      data-testid={`vendor-make-purchase-tab-${id}`}
    >
      <Icon size={12} /> {label}
      {disabled && <span className="text-[9px] uppercase tracking-wide ml-1 opacity-70">soon</span>}
    </button>
  );
}

// ---------- Carat material purchase (Diamond / Color Stone / Pearl) ----------
// Per-material routing — keep all API paths + UX labels in one place so the
// shared form below stays DRY across all three carat-based modules.
const CARAT_CFG = {
  diamond: {
    label: 'diamond',
    labelTitle: 'Diamond',
    basePath: '/diamonds',
    materialLabel: 'Diamond',
    testidPrefix: 'vendor-diamond',
  },
  color_stone: {
    label: 'color stone',
    labelTitle: 'Color Stone',
    basePath: '/color-stones',
    materialLabel: 'Color Stone',
    testidPrefix: 'vendor-color-stone',
  },
  pearl: {
    label: 'pearl',
    labelTitle: 'Pearl',
    basePath: '/pearls',
    materialLabel: 'Pearl',
    testidPrefix: 'vendor-pearl',
  },
};

function CaratPurchaseForm({ material = 'diamond', vendor, companyId, onPurchased }) {
  const cfg = CARAT_CFG[material] || CARAT_CFG.diamond;
  const tp = cfg.testidPrefix;
  const [classifications, setClassifications] = useState([]);
  const [packets, setPackets] = useState([]);
  const [classDlgOpen, setClassDlgOpen] = useState(false);
  const [mode, setMode] = useState('existing'); // 'existing' | 'new'
  const [packetId, setPacketId] = useState('');
  const [form, setForm] = useState({
    carats: '', pieces: '', rate_per_ct: '', reference: '', note: '',
  });
  const [newPkt, setNewPkt] = useState({
    classification_id: '', valuation_mode: 'fixed', purchase_status: 'purchased',
    description: '',
  });
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setForm({ carats: '', pieces: '', rate_per_ct: '', reference: '', note: '' });
    setPacketId(''); setNewPkt((p) => ({ ...p, description: '' }));
  };

  const loadAll = async () => {
    const [c, p] = await Promise.all([
      api.get(`${cfg.basePath}/classifications`, { params: { company_id: companyId } }),
      api.get(`${cfg.basePath}/packets`, { params: { company_id: companyId } }),
    ]);
    setClassifications(c.data); setPackets(p.data);
  };
  // Reload whenever the active material or company changes so a quick tab
  // switch (e.g. Diamond → Pearls) pulls the right classifications/packets.
  useEffect(() => { if (companyId) { reset(); loadAll(); } /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [companyId, material]);

  const selectedPkt = packets.find((p) => p.id === packetId);
  const fixedLocked = selectedPkt && selectedPkt.valuation_mode === 'fixed' && Number(selectedPkt.fixed_rate_per_ct || 0) > 0;

  const submitExisting = async () => {
    const ct = Number(form.carats); const pcs = Number(form.pieces);
    if (!packetId) { toast.error('Select a packet'); return; }
    if (!ct && !pcs) { toast.error('Enter carats or pieces'); return; }
    if (selectedPkt?.valuation_mode === 'running_avg' && !Number(form.rate_per_ct)) {
      toast.error('Rate required for Running Average packets'); return;
    }
    setSaving(true);
    try {
      const payload = {
        type: 'receipt', carats: ct, pieces: pcs,
        vendor_id: vendor.id, vendor_name: vendor.name,
        reference: form.reference, note: form.note,
      };
      if (!fixedLocked && form.rate_per_ct) payload.rate_per_ct = Number(form.rate_per_ct);
      await api.post(`${cfg.basePath}/packets/${packetId}/receipt`, payload);
      toast.success(`Receipt added to ${selectedPkt?.packet_no || 'packet'}`);
      reset(); await loadAll(); onPurchased && onPurchased();
    } catch (e) { toast.error(extractError(e, 'Receipt failed')); }
    finally { setSaving(false); }
  };

  const submitNew = async () => {
    if (!newPkt.classification_id) { toast.error('Select classification'); return; }
    const ct = Number(form.carats); const pcs = Number(form.pieces);
    if (!ct && !pcs) { toast.error('Enter opening carats or pieces'); return; }
    if (!Number(form.rate_per_ct)) { toast.error('Rate required'); return; }
    setSaving(true);
    try {
      const { data: pkt } = await api.post(`${cfg.basePath}/packets`, {
        classification_id: newPkt.classification_id,
        valuation_mode: newPkt.valuation_mode,
        initial_carats: ct,
        initial_pieces: pcs,
        initial_rate_per_ct: Number(form.rate_per_ct),
        vendor_id: vendor.id, vendor_name: vendor.name,
        description: newPkt.description,
        purchase_status: newPkt.purchase_status,
        company_id: companyId,
      });
      toast.success(`Packet ${pkt.packet_no} created with opening stock`);
      reset(); await loadAll(); onPurchased && onPurchased();
    } catch (e) { toast.error(extractError(e, 'Packet create failed')); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <button type="button" onClick={() => setMode('existing')}
          className={`px-3 py-1.5 text-xs rounded-md border ${mode === 'existing' ? 'border-[var(--ink-navy)] bg-[var(--ink-navy)] text-white' : 'border-[var(--divider)] hover:bg-slate-50'}`}
          data-testid={`${tp}-mode-existing`}>Add to Existing Packet</button>
        <button type="button" onClick={() => setMode('new')}
          className={`px-3 py-1.5 text-xs rounded-md border ${mode === 'new' ? 'border-[var(--ink-navy)] bg-[var(--ink-navy)] text-white' : 'border-[var(--divider)] hover:bg-slate-50'}`}
          data-testid={`${tp}-mode-new`}>Create New Packet</button>
      </div>

      {mode === 'existing' ? (
        <div className="space-y-3">
          <div>
            <Label>Packet</Label>
            <SearchableSelect
              items={packets}
              value={packetId}
              onValueChange={(v) => setPacketId(v || '')}
              itemToLabel={(p) => `${p.packet_no} · ${p.classification?.shape_name || p.classification_shape || ''} · ${p.valuation_mode === 'fixed' ? 'Fixed' : 'Run.Avg'}`}
              searchKeys={['packet_no', 'classification_shape', 'description']}
              placeholder={packets.length === 0 ? 'No packets yet' : 'Select existing packet…'}
              searchPlaceholder="Search packet no / shape…"
              disabled={packets.length === 0}
              testId={`${tp}-packet-select`}
            />
          </div>
          {selectedPkt && (
            <div className="text-[11px] text-slate-500 px-2 py-1 rounded bg-[var(--paper-warm)]">
              Balance: {formatCarats(selectedPkt.balance_carats)} ct · {selectedPkt.balance_pieces} pcs
              {fixedLocked && <> · Fixed Rate ₹{Number(selectedPkt.fixed_rate_per_ct).toLocaleString('en-IN')}/ct (locked)</>}
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Carats</Label><Input type="number" step="0.01" value={form.carats} onChange={(e) => setForm({ ...form, carats: e.target.value })} data-testid={`${tp}-carats-input`} /></div>
            <div><Label>Pieces</Label><Input type="number" step="1" value={form.pieces} onChange={(e) => setForm({ ...form, pieces: e.target.value })} data-testid={`${tp}-pieces-input`} /></div>
            <div>
              <Label>Rate ₹/ct {fixedLocked && '(locked)'}</Label>
              <Input
                type="number" step="0.01"
                value={fixedLocked ? Number(selectedPkt.fixed_rate_per_ct || 0) : form.rate_per_ct}
                onChange={(e) => setForm({ ...form, rate_per_ct: e.target.value })}
                disabled={fixedLocked}
                className={fixedLocked ? 'bg-slate-100 cursor-not-allowed' : ''}
                data-testid={`${tp}-rate-input`}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid={`${tp}-reference-input`} /></div>
            <div><Label>Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid={`${tp}-note-input`} /></div>
          </div>
          <div className="flex justify-end">
            <Button onClick={submitExisting} disabled={saving || !packetId} className="luxury-button" data-testid={`${tp}-submit-existing`}>{saving ? 'Saving…' : 'Record Receipt'}</Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Classification</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <SearchableSelect
                    items={classifications}
                    value={newPkt.classification_id}
                    onValueChange={(v) => setNewPkt({ ...newPkt, classification_id: v || '' })}
                    itemToLabel={(c) => `${c.schema_code} — ${c.shape_name}`}
                    searchKeys={['schema_code', 'shape_name']}
                    placeholder={classifications.length === 0 ? 'No classifications — click + to create' : 'Select shape…'}
                    searchPlaceholder="Search shape / code…"
                    disabled={classifications.length === 0}
                    testId={`${tp}-new-classification`}
                  />
                </div>
                <Button type="button" variant="outline" size="icon" onClick={() => setClassDlgOpen(true)} title={`Create ${cfg.label} classification`} data-testid={`${tp}-create-classification-button`}>
                  <Plus size={14} />
                </Button>
              </div>
            </div>
            <div>
              <Label>Valuation Mode</Label>
              <Select value={newPkt.valuation_mode} onValueChange={(v) => setNewPkt({ ...newPkt, valuation_mode: v })}>
                <SelectTrigger data-testid={`${tp}-new-valuation`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fixed">Fixed Rate</SelectItem>
                  <SelectItem value="running_avg">Running Average</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Opening Carats</Label><Input type="number" step="0.01" value={form.carats} onChange={(e) => setForm({ ...form, carats: e.target.value })} data-testid={`${tp}-new-carats`} /></div>
            <div><Label>Opening Pieces</Label><Input type="number" step="1" value={form.pieces} onChange={(e) => setForm({ ...form, pieces: e.target.value })} data-testid={`${tp}-new-pieces`} /></div>
            <div><Label>Rate ₹/ct</Label><Input type="number" step="0.01" value={form.rate_per_ct} onChange={(e) => setForm({ ...form, rate_per_ct: e.target.value })} data-testid={`${tp}-new-rate`} /></div>
          </div>
          <div>
            <Label>Purchase Status</Label>
            <Select value={newPkt.purchase_status} onValueChange={(v) => setNewPkt({ ...newPkt, purchase_status: v })}>
              <SelectTrigger data-testid={`${tp}-new-purchase-status`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="purchased">Stock (Purchased) — posts to payable</SelectItem>
                <SelectItem value="on_approval">On Approval — held until conversion</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Description (optional)</Label>
            <Input value={newPkt.description} onChange={(e) => setNewPkt({ ...newPkt, description: e.target.value })} data-testid={`${tp}-new-description`} />
          </div>
          {form.carats && form.rate_per_ct && (
            <Card className="p-3 bg-[var(--paper-warm)] border-[var(--divider)]">
              <div className="text-xs text-slate-500">Opening valuation preview</div>
              <div className="font-mono text-sm text-[var(--ink-navy)]">
                {formatCarats(form.carats)} ct × ₹{Number(form.rate_per_ct).toLocaleString('en-IN')}/ct = {formatINR(Number(form.carats) * Number(form.rate_per_ct))}
              </div>
            </Card>
          )}
          <div className="flex justify-end">
            <Button onClick={submitNew} disabled={saving || !newPkt.classification_id} className="luxury-button" data-testid={`${tp}-submit-new`}>{saving ? 'Saving…' : 'Create Packet & Record'}</Button>
          </div>
        </div>
      )}

      <ClassificationFormDialog
        open={classDlgOpen}
        onOpenChange={setClassDlgOpen}
        companyId={companyId}
        material={material}
        onCreated={async (cls) => { await loadAll(); setNewPkt((p) => ({ ...p, classification_id: cls.id })); }}
      />
    </div>
  );
}

// ---------- Gold purchase ----------
function GoldPurchaseForm({ vendor, companyId, onPurchased }) {
  const [purities, setPurities] = useState([]);
  const [rateInfo, setRateInfo] = useState(null);
  const [purityDlgOpen, setPurityDlgOpen] = useState(false);
  const [form, setForm] = useState({
    weight_grams: '', purity_percent: '100', rate_per_gram_999: '',
    reference: '', note: '',
    timestamp: nowISTDatetimeLocal(),
  });
  const [saving, setSaving] = useState(false);

  const loadPurities = async () => {
    const { data } = await api.get('/gold/purities', { params: { company_id: companyId } });
    setPurities(data);
  };
  useEffect(() => { if (companyId) loadPurities(); }, [companyId]);

  useEffect(() => {
    let cancelled = false;
    async function lookup() {
      try {
        const { data } = await api.get('/gold/rates/lookup', { params: { date: istDatetimeLocalToISO(form.timestamp), company_id: companyId } });
        if (!cancelled) setRateInfo(data);
      } catch { /* noop */ }
    }
    if (companyId && form.timestamp) lookup();
    return () => { cancelled = true; };
  }, [form.timestamp, companyId]);

  const submit = async () => {
    if (!Number(form.weight_grams)) { toast.error('Weight required'); return; }
    setSaving(true);
    try {
      const payload = {
        type: 'receipt',
        weight_grams: Number(form.weight_grams),
        purity_percent: Number(form.purity_percent),
        party_type: 'vendor', party_id: vendor.id, party_name: vendor.name,
        reference: form.reference, note: form.note,
        timestamp: istDatetimeLocalToISO(form.timestamp),
        company_id: companyId,
      };
      const r = Number(form.rate_per_gram_999);
      if (form.rate_per_gram_999 && r > 0) payload.rate_per_gram_999 = r;
      await api.post('/gold/ledger', payload);
      toast.success('Gold receipt recorded');
      setForm({ ...form, weight_grams: '', reference: '', note: '', rate_per_gram_999: '' });
      onPurchased && onPurchased();
    } catch (e) { toast.error(extractError(e, 'Failed')); }
    finally { setSaving(false); }
  };

  const previewValue = (() => {
    const w = Number(form.weight_grams) || 0;
    const p = Number(form.purity_percent) || 0;
    const r = Number(form.rate_per_gram_999) || (rateInfo?.available ? Number(rateInfo.rate_per_gram_999) : 0);
    return w * (p / 100) * r;
  })();

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Weight (g)</Label><Input type="number" step="0.001" value={form.weight_grams} onChange={(e) => setForm({ ...form, weight_grams: e.target.value })} data-testid="vendor-gold-weight-input" /></div>
        <div>
          <Label>Purity %</Label>
          <div className="flex gap-2">
            <Select value={String(form.purity_percent)} onValueChange={(v) => setForm({ ...form, purity_percent: v })}>
              <SelectTrigger data-testid="vendor-gold-purity-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="100">999 (100% pure)</SelectItem>
                {purities.map((p) => <SelectItem key={p.id} value={String(p.percent)}>{p.name} ({p.percent}%)</SelectItem>)}
              </SelectContent>
            </Select>
            <Button type="button" variant="outline" size="icon" onClick={() => setPurityDlgOpen(true)} title="Create purity" data-testid="vendor-gold-create-purity-button"><Plus size={14} /></Button>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Rate ₹/g 999 <span className="text-slate-400">(optional)</span></Label>
          <Input
            type="number" step="0.01"
            value={form.rate_per_gram_999}
            onChange={(e) => setForm({ ...form, rate_per_gram_999: e.target.value })}
            placeholder={rateInfo?.available ? `Default ₹${Number(rateInfo.rate_per_gram_999).toLocaleString('en-IN')}/g (system)` : 'Use latest system rate'}
            data-testid="vendor-gold-rate-input"
          />
        </div>
        <div><Label>Timestamp</Label><Input type="datetime-local" value={form.timestamp} onChange={(e) => setForm({ ...form, timestamp: e.target.value })} /></div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Reference</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="vendor-gold-reference-input" /></div>
        <div><Label>Note</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="vendor-gold-note-input" /></div>
      </div>
      {previewValue > 0 && (
        <Card className="p-3 bg-[var(--paper-warm)] border-[var(--divider)]">
          <div className="text-xs text-slate-500">Estimated value</div>
          <div className="font-mono text-sm text-[var(--ink-navy)]">{formatINR(previewValue)}</div>
        </Card>
      )}
      {rateInfo && !rateInfo.available && !form.rate_per_gram_999 && (
        <Card className="p-3 bg-rose-50 border-rose-200">
          <div className="text-xs text-rose-700">No gold rate available — enter a rate above or add one in the Gold » Rates tab.</div>
        </Card>
      )}
      <div className="flex justify-end">
        <Button onClick={submit} disabled={saving || !Number(form.weight_grams)} className="luxury-button" data-testid="vendor-gold-submit">{saving ? 'Saving…' : 'Record Gold Receipt'}</Button>
      </div>
      <PurityFormDialog
        open={purityDlgOpen}
        onOpenChange={setPurityDlgOpen}
        companyId={companyId}
        onCreated={async (p) => { await loadPurities(); setForm((f) => ({ ...f, purity_percent: String(p.percent) })); }}
      />
    </div>
  );
}

export default MakePurchaseInline;
